<template>
  <div class="search-module" data-cy="search-module">
    <div
      v-for="(item, ind) in itemsToShow"
      class="search-module__item"
      :key="ind"
      data-cy="search-module-item"
    >
      <header class="search-module__header" data-cy="search-module-title">
        {{ item.title }}
      </header>
      <div class="search-module__content">
        <TableSearch
          class="search-module__search"
          :placeholder="item.placeholder"
          :value="item.value"
          @searchSubmitted="item.onSubmit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { UserRoles } from "@/Domain/UserRoles";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import { useMainStore } from "@/stores/useMainStore";
import { computed, ref, type ComputedRef } from "vue";

interface SearchItem {
  title: string;
  placeholder: string;
  value: string;
  onSubmit: (value: string) => Promise<void> | void;
  show?: ComputedRef<boolean>;
}

const main = useMainStore();

const showUkdSearch = computed(() => {
  return (
    (main.isEmulator ||
      (main.companySummary &&
        main.companySummary.denials &&
        main.companySummary.denials.every(
          (d) => !d.UKD || d.UKD.toUpperCase() !== "ALL"
        )) ||
      (!main.companySummary &&
        main.isInAnyRole(UserRoles.ROLES_UDB_APPLICATION))) ??
    false
  );
});

const showRcSearch = computed(() => {
  return main.isInAnyRole(UserRoles.ROLES_ALL_RFR);
});

const items = ref<SearchItem[]>([
  {
    title: "Search Universal Kosher Database (UKD) for certified ingredients",
    placeholder: "Search by Name or Company or Brand or UKD ID",
    value: main.UkdProductFilterCriteria,
    onSubmit: (value: string) =>
      setSearchValueAndRedirect(value, "UkdProductList"),
    show: showUkdSearch,
  },
  {
    title: "OU Company Search",
    placeholder: "Search by Company Name",
    value: main.UkdCompanyFilterCriteria,
    onSubmit: (value: string) =>
      setSearchValueAndRedirect(value, "UkdCompanyList"),
  },
  {
    title: "OU Copacker Directory",
    placeholder: "Search by Name, Industry, Description or Location",
    value: "",
    onSubmit: (value: string) =>
      setSearchValueAndRedirect(value, "CoPackerFacilities"),
  },
  {
    title: "Search for Company RC Info",
    placeholder: "Search by Company or RC Name",
    value: "",
    onSubmit: (value: string) =>
      setSearchValueAndRedirect(value, "CompanyRcList"),
    show: showRcSearch,
  },
]);

const itemsToShow = computed(() => {
  return items.value.filter((i) => i.show !== false);
});

const setSearchValueAndRedirect = (value: string, url: string) => {
  window.location.assign(url + `?search=${value}`);
};
</script>

<style scoped lang="scss">
.search-module {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__item {
    border: 1px solid #e8e8e8;
    background: white;
  }

  &__header {
    padding: 8px 15px;
    font-size: 13px;
    background: #efefef;
    border-bottom: 1px solid #e8e8e8;
    position: relative;
  }

  &__content {
    padding: 15px;
  }

  &__search {
    width: 100%;
  }
}
</style>
